import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useDetections } from 'src/data-capture/hooks/file-uploads/useDetections';
import { useFamilyFileActions } from 'src/data-capture/file-management/family/useFamilyFileActions';
import { useDetectedEntitiesStore } from 'src/data-capture/stores/useDetectedEntitiesStore';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useFileUpload } from './useFileUpload';
import { useFamilyReviewId } from './useFamilyReviewId';

type UseFileUploadWithEntitiesProps = {
  autoRefreshProcessing?: boolean;
};

export function useFileUploadWithEntities({
  autoRefreshProcessing = false,
}: UseFileUploadWithEntitiesProps = {}) {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId') ?? '';
  const familyId = useFamilyReviewId();
  const { clientId } = useCurrentClientIfAvailable();

  const { handleDetectionData } = useDetectedEntitiesStore();
  const { flagEnabledShowNonConflictingFields, flagEnabledPreventAddConflicts } = useFlags();

  const {
    file,
    isLoading: isFileLoading,
    error: fileError,
    setFileResolved,
  } = useFileUpload(fileId, { autoRefreshProcessing, familyId });

  const { finaliseMember } = useFamilyFileActions({ familyId: familyId ?? '' });

  const {
    detections,
    isLoading: isDetectLoading,
    isFetching: isDetectFetching,
    error: detectError,
    refetchDetections,
  } = useDetections({
    fileId: file?.id,
  });

  useEffect(() => {
    if (file != null) {
      handleDetectionData(file.id, detections, flagEnabledShowNonConflictingFields, {
        excludeInsertedEntityConflicts: flagEnabledPreventAddConflicts,
      });
    }
  }, [
    file?.id,
    detections,
    isDetectFetching,
    flagEnabledShowNonConflictingFields,
    flagEnabledPreventAddConflicts,
  ]);

  const isLoading = isFileLoading || isDetectLoading;
  const error = fileError || detectError;

  const refetch = async () => {
    await refetchDetections();
  };

  const finaliseReview = async () => {
    if (familyId && file && clientId) {
      await finaliseMember(file, clientId);
      return;
    }
    await setFileResolved(true);
  };

  return {
    file,
    isLoading,
    error,
    refetch,
    finaliseReview,
  };
}
